import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Link } from "react-router";
import type { HouseholdListItem } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/shared/components/ui/AlertDialog";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { useDeleteHousehold, useHouseholdsList } from "@/shared/lib/households";
import { cn } from "@/shared/lib/utils";

const PAGE_LIMIT = 50;

export function HouseholdsList() {
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursorStack[cursorStack.length - 1];
  const list = useHouseholdsList({ cursor, limit: PAGE_LIMIT });
  const page = cursorStack.length - 1;

  function nextPage() {
    if (list.data?.nextCursor) {
      setCursorStack([...cursorStack, list.data.nextCursor]);
    }
  }
  function prevPage() {
    if (cursorStack.length > 1) setCursorStack(cursorStack.slice(0, -1));
  }

  const items = list.data?.items ?? [];

  return (
    <Section spacing="md">
      <Container size="6xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to="/app/contacts"
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to contacts
            </Link>
            <div className="flex items-baseline justify-between gap-4">
              <h1 className="type-heading-lg text-text">Households</h1>
              <Button asChild size="sm">
                <Link to="/app/contacts/households/new">New household</Link>
              </Button>
            </div>
          </div>

          {list.isLoading ? (
            <p className="type-body-base text-text-muted">Loading…</p>
          ) : list.isError ? (
            <p className="type-body-base text-danger-strong">
              Couldn't load households: {list.error.message}
            </p>
          ) : items.length === 0 ? (
            <EmptyState
              title="No households yet"
              description="Group couples, families, and roommates so a single invitation goes to one address."
              action={
                <Button asChild>
                  <Link to="/app/contacts/households/new">Create a household</Link>
                </Button>
              }
            />
          ) : (
            <HouseholdsTable items={items} />
          )}

          {(list.data?.nextCursor || page > 0) && !list.isLoading ? (
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={prevPage} disabled={page === 0}>
                Previous
              </Button>
              <p className="type-body-sm text-text-muted">Page {page + 1}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={!list.data?.nextCursor}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

function householdName(hh: HouseholdListItem): string {
  return hh.displayNameCustom ?? "Unnamed household";
}

const HH_GRID =
  "grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_minmax(0,1fr)_2.5rem] items-center gap-4";

function HouseholdsTable({ items }: { items: HouseholdListItem[] }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          HH_GRID,
          "px-4 type-label-sm tracking-wider uppercase text-text-muted",
        )}
      >
        <span>Household</span>
        <span>Address</span>
        <span>Members</span>
        <span className="sr-only">Actions</span>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((hh) => (
          <li
            key={hh.id}
            className={cn(
              HH_GRID,
              "px-4 rounded-lg glass-surface-subtle hover:glass-surface transition-colors duration-150",
            )}
          >
            <Link
              to={`/app/contacts/households/${hh.id}`}
              className="grid grid-cols-subgrid col-span-3 items-center py-3"
            >
              <span className="type-body-base text-text truncate">
                {householdName(hh)}
              </span>
              <span className="type-body-sm text-text-muted truncate">
                {hh.address?.line1 ?? "—"}
              </span>
              <span className="type-body-sm text-text-muted">
                {hh.memberCount} {hh.memberCount === 1 ? "member" : "members"}
              </span>
            </Link>
            <DeleteHouseholdButton household={hh} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeleteHouseholdButton({ household }: { household: HouseholdListItem }) {
  const del = useDeleteHousehold();
  const [open, setOpen] = useState(false);
  const name = householdName(household);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost-danger" size="icon" aria-label={`Delete ${name}`}>
          <Trash2 aria-hidden className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            The household is removed. Member contacts remain, but lose their
            household assignment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              onClick={() => del.mutate(household.id)}
              disabled={del.isPending}
            >
              {del.isPending ? "Deleting…" : "Delete household"}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
